/**
 * JobForm.jsx — Modal-style form for creating/editing a job (MUI)
 */

import { useState, useEffect, useMemo } from 'react';
import { Box, Paper, Typography, TextField, Button, MenuItem, Select, InputLabel, FormControl, Grid, Stack, Alert, List, ListItemButton, ListItemText, Chip, IconButton, FormControlLabel, Checkbox } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { capitalize } from '../../lib/utils';
import { useCreateJob, useUpdateJob } from '../../hooks/useJobs';
import { useContacts } from '../../hooks/useContacts';
import AddressAutocomplete from '../shared/AddressAutocomplete';
import { useToast } from '../layout/Toast';

const JOB_TYPES = ['Real Estate Video', 'FPV Tour', 'Matterport', 'Luxury Film', 'Event', 'VirTour', 'Other'];
const STATUS_OPTIONS = ['intake', 'quoted', 'confirmed', 'shooting', 'post_production', 'partial_delivery', 'delivered', 'invoiced', 'paid'];

export default function JobForm({ initialJob = null, prefill = null, onSave, onCancel }) {
  const { toast } = useToast();
  const createMutation = useCreateJob();
  const updateMutation = useUpdateJob();
  const { data: allContacts = [] } = useContacts();

  const [contactSearch, setContactSearch] = useState('');
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState(initialJob?.contactId || prefill?.contactId || null);

  const contactResults = useMemo(() => {
    if (!contactSearch || contactSearch.length < 1) return [];
    const q = contactSearch.toLowerCase();
    return allContacts.filter((c) => (c.name || '').toLowerCase().includes(q) || (c.company || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q)).slice(0, 8);
  }, [contactSearch, allContacts]);

  const handleSelectContact = (contact) => {
    setSelectedContactId(contact.id);
    setFormData((prev) => ({ ...prev, clientName: contact.name || '', contactName: contact.name || '', contactEmail: contact.email || '', contactPhone: contact.phone || '' }));
    setContactSearch(''); setShowContactDropdown(false);
  };

  const [formData, setFormData] = useState({
    title: '', clientName: '', contactName: '', contactEmail: '', contactPhone: '',
    jobType: '', location: '', shootDate: '', shootDates: [], deliveryDate: '', days: '',
    revenue: '', discount: '', status: 'intake', notes: '', deliverables: '',
    originator: '', referredBy: '', deliveryLinks: '', workflowType: 'standard',
  });
  const [newShootDate, setNewShootDate] = useState('');

  useEffect(() => {
    const src = initialJob || prefill;
    if (src) {
      setFormData(prev => ({
        ...prev,
        title: src.title || prev.title, clientName: src.clientName || prev.clientName, contactName: src.contactName || prev.contactName,
        contactEmail: src.contactEmail || prev.contactEmail, contactPhone: src.contactPhone || prev.contactPhone,
        jobType: src.jobType || prev.jobType, location: src.location || prev.location,
        shootDate: src.shootDate || prev.shootDate, shootDates: (src.shootDates && src.shootDates.length > 0) ? src.shootDates : (src.shootDate ? [src.shootDate] : prev.shootDates),
        deliveryDate: src.deliveryDate || prev.deliveryDate, days: src.days || prev.days,
        revenue: src.revenue || prev.revenue, discount: src.discount || prev.discount, status: src.status || prev.status,
        notes: src.notes || prev.notes, deliverables: src.deliverables || prev.deliverables,
        originator: src.originator || prev.originator, referredBy: src.referredBy || prev.referredBy, deliveryLinks: src.deliveryLinks || prev.deliveryLinks,
        workflowType: src.workflowType || prev.workflowType,
      }));
    }
  }, [initialJob, prefill]);

  const handleChange = (e) => { const { name, value } = e.target; setFormData((prev) => ({ ...prev, [name]: value })); };

  const addShootDate = () => {
    if (!newShootDate || formData.shootDates.includes(newShootDate)) return;
    const dates = [...formData.shootDates, newShootDate].sort();
    setFormData(prev => ({ ...prev, shootDates: dates, shootDate: dates[0], days: String(dates.length) }));
    setNewShootDate('');
  };
  const removeShootDate = (d) => {
    const dates = formData.shootDates.filter(x => x !== d);
    setFormData(prev => ({ ...prev, shootDates: dates, shootDate: dates[0] || '', days: String(dates.length || '') }));
  };
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!formData.title.trim()) errs.title = 'Title is required';
    if (formData.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) errs.contactEmail = 'Invalid email format';
    if (formData.revenue && (isNaN(parseFloat(formData.revenue)) || parseFloat(formData.revenue) < 0)) errs.revenue = 'Revenue must be a positive number';
    const lastShoot = formData.shootDates.length > 0 ? formData.shootDates[formData.shootDates.length - 1] : formData.shootDate;
    if (lastShoot && formData.deliveryDate && lastShoot > formData.deliveryDate) errs.deliveryDate = 'Delivery date must be after last shoot date';
    setErrors(errs);
    if (Object.keys(errs).length > 0) { toast(Object.values(errs)[0], 'error'); return false; }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      const payload = { ...formData, contactId: selectedContactId || null };
      if (initialJob) { await updateMutation.mutateAsync({ id: initialJob.id, ...payload }); toast('Job updated', 'success'); }
      else { await createMutation.mutateAsync(payload); toast('Job created', 'success'); }
      onSave?.();
    } catch (err) { toast(`Error: ${err.message}`, 'error'); }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Paper sx={{ p: 3, maxHeight: '100%', overflowY: 'auto' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>{initialJob ? 'Edit Job' : 'New Job'}</Typography>

      <Grid container spacing={2}>
        {/* Title */}
        <Grid size={12}>
          <TextField fullWidth size="small" label="Title *" name="title" value={formData.title} onChange={handleChange} error={!!errors.title} helperText={errors.title} />
        </Grid>

        {/* Client / Contact Picker */}
        <Grid size={12}>
          <Box sx={{ position: 'relative' }}>
            <TextField
              fullWidth size="small"
              label={selectedContactId ? 'Client / Contact (Linked)' : 'Client / Contact'}
              name="clientName" value={formData.clientName}
              onChange={(e) => { handleChange(e); setContactSearch(e.target.value); setShowContactDropdown(true); if (selectedContactId) setSelectedContactId(null); }}
              onFocus={() => { if (formData.clientName) { setContactSearch(formData.clientName); setShowContactDropdown(true); } }}
              onBlur={() => setTimeout(() => setShowContactDropdown(false), 200)}
              placeholder="Type to search contacts..."
              color={selectedContactId ? 'primary' : undefined}
              slotProps={{ input: { endAdornment: selectedContactId ? <Chip size="small" label="Linked" color="primary" onDelete={() => setSelectedContactId(null)} /> : null } }}
            />
            {showContactDropdown && contactResults.length > 0 && (
              <Paper sx={{ position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, mt: 0.5, maxHeight: 200, overflowY: 'auto' }} elevation={8}>
                <List dense disablePadding>
                  {contactResults.map((c) => (
                    <ListItemButton key={c.id} data-track="job.contactPick" onMouseDown={(e) => e.preventDefault()} onClick={() => handleSelectContact(c)}>
                      <ListItemText primary={c.name} secondary={c.company || c.email} slotProps={{ primary: { variant: 'body2', fontWeight: 600 }, secondary: { variant: 'caption' } }} />
                      <Typography variant="caption" color="text.disabled">{c.type}</Typography>
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            )}
          </Box>
        </Grid>

        {/* Email / Phone */}
        <Grid size={6}><TextField fullWidth size="small" label="Email" name="contactEmail" type="email" value={formData.contactEmail} onChange={handleChange} error={!!errors.contactEmail} helperText={errors.contactEmail} /></Grid>
        <Grid size={6}><TextField fullWidth size="small" label="Phone" name="contactPhone" type="tel" value={formData.contactPhone} onChange={handleChange} /></Grid>

        {/* Type / Location */}
        <Grid size={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Type</InputLabel>
            <Select name="jobType" value={formData.jobType} onChange={handleChange} label="Type">
              <MenuItem value=""><em>Select type...</em></MenuItem>
              {JOB_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={6}><AddressAutocomplete variant="mui" label="Location" value={formData.location} onChange={v => handleChange({ target: { name: 'location', value: v } })} /></Grid>

        {/* Staycation workflow flag — orthogonal to Type; drives the segment pay
            split + post-only pipeline. */}
        <Grid size={12}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={formData.workflowType === 'staycation'}
                onChange={(e) => setFormData(prev => ({ ...prev, workflowType: e.target.checked ? 'staycation' : 'standard' }))}
              />
            }
            label="Staycation workflow (segment-based pay split, post-only pipeline)"
          />
        </Grid>

        {/* Shoot Dates — multi-day */}
        <Grid size={12}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Shoot Dates</Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: formData.shootDates.length ? 1 : 0 }}>
            <TextField size="small" type="date" value={newShootDate} onChange={e => setNewShootDate(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} sx={{ width: 180 }} />
            <IconButton size="small" onClick={addShootDate} disabled={!newShootDate} color="primary"><AddIcon fontSize="small" /></IconButton>
          </Stack>
          {formData.shootDates.length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {formData.shootDates.map((d, i) => (
                <Chip key={d} label={`Day ${i + 1}: ${d}`} size="small" onDelete={() => removeShootDate(d)} deleteIcon={<CloseIcon fontSize="small" />} sx={{ mb: 0.5 }} />
              ))}
            </Stack>
          )}
        </Grid>
        {/* Delivery Date */}
        <Grid size={6}><TextField fullWidth size="small" label="Delivery Date" name="deliveryDate" type="date" value={formData.deliveryDate} onChange={handleChange} error={!!errors.deliveryDate} helperText={errors.deliveryDate} slotProps={{ inputLabel: { shrink: true } }} /></Grid>
        <Grid size={6} />

        {/* Days / Revenue */}
        <Grid size={6}><TextField fullWidth size="small" label="Days" name="days" type="number" value={formData.days} onChange={handleChange} /></Grid>
        <Grid size={6}><TextField fullWidth size="small" label="Revenue" name="revenue" type="number" value={formData.revenue} onChange={handleChange} error={!!errors.revenue} helperText={errors.revenue} slotProps={{ input: { inputProps: { step: '0.01' } } }} /></Grid>

        {/* Discount / Status */}
        <Grid size={6}><TextField fullWidth size="small" label="Discount" name="discount" type="number" value={formData.discount} onChange={handleChange} slotProps={{ input: { inputProps: { step: '0.01' } } }} /></Grid>
        <Grid size={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select name="status" value={formData.status} onChange={handleChange} label="Status">
              {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s}>{capitalize(s)}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>

        {/* Originator / Referred By */}
        <Grid size={6}><TextField fullWidth size="small" label="Originator" name="originator" value={formData.originator} onChange={handleChange} /></Grid>
        <Grid size={6}><TextField fullWidth size="small" label="Referred By" name="referredBy" value={formData.referredBy} onChange={handleChange} /></Grid>

        {/* Notes */}
        <Grid size={12}><TextField fullWidth size="small" label="Notes" name="notes" value={formData.notes} onChange={handleChange} multiline rows={3} /></Grid>

        {/* Deliverables */}
        <Grid size={12}><TextField fullWidth size="small" label="Deliverables" name="deliverables" value={formData.deliverables} onChange={handleChange} multiline rows={3} /></Grid>

        {/* Delivery Links */}
        <Grid size={12}><TextField fullWidth size="small" label="Delivery Links" name="deliveryLinks" value={formData.deliveryLinks} onChange={handleChange} multiline rows={2} /></Grid>
      </Grid>

      {/* Buttons */}
      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button variant="outlined" size="small" onClick={onCancel} disabled={isLoading}>Cancel</Button>
        <Button variant="contained" size="small" onClick={handleSave} disabled={isLoading}>{isLoading ? 'Saving...' : 'Save Job'}</Button>
      </Stack>
    </Paper>
  );
}
